import * as React from 'react';
import { Alert, Tag } from 'antd';
import { TagType } from 'antd/lib/tag';
import _, { random } from 'lodash';

import { RolloutTarget } from '../../../../lib/PlannerDTOs';
import { RolloutDiffValue } from './RolloutDiffValue';

export const DIFF_SHOWFOR = {
  ALL: 'all',
  BEFORE: 'before',
  AFTER: 'after',
} as const;

export type DiffShowFor_Keys = keyof typeof DIFF_SHOWFOR;

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object: any, base: any): any {
  // eslint-disable-next-line @typescript-eslint/ban-types
  let clone: {} = JSON.parse(JSON.stringify(base));
  function changes(object, base) {
    return _.transform(
      object,
      function (result, value, key) {
        if (!_.isEqual(value, base[key])) {
          result[key] =
            _.isObject(value) && _.isObject(base[key])
              ? changes(value, base[key])
              : value;
        }
        delete clone[key];
      },
      {},
    );
  }
  let res = changes(object, base);
  for (const k in clone) {
    clone[k] = null;
  }
  return { ...res };
}

/**
 * Generate Diff Component for value in rollout target
 * @param  {string} varType Variable type for string formatting of values
 * @param  {RolloutTarget} compareTarget RolloutTarget compared
 * @param  {RolloutTarget} baseTarget   RolloutTarget to compare with
 * @return {React.ReactNode}        Return a RolloutDiffValue component which represents the value diff
 */
export function generateValueDiff(
  varType: string,
  compareTarget: RolloutTarget,
  baseTarget: RolloutTarget,
) {
  const { value, string, script } = baseTarget;
  const diff = difference(compareTarget, baseTarget);
  return (
    <RolloutDiffValue
      genericValue={{ value, string, script }}
      varType={varType}
      diff={diff}
    />
  );
}

/**
 * Generate Formats colour for number
 * @param  {number} after number compared
 * @param  {number} before   number to compare with
 * @return {React.ReactNode}        Return a p tag of the number with appropriate className
 */
export function formatColorNumber(after: number, before: number): any {
  const className =
    after != before ? 'rollout-target__title-modified' : undefined;
  return <p className={className}>{after}</p>;
}

/**
 * Generate Formats colour for boolean
 * @param  {number} after number compared
 * @param  {number} before   number to compare with
 * @return {React.ReactNode}        Return a p tag of the number with appropriate className
 */
export function formatColorBoolean(after: number, before: number): any {
  const className =
    after != before ? 'rollout-target__title-modified' : undefined;
  const afterBool = after == 1 ? 'true' : 'false';
  return <p className={className}>{afterBool}</p>;
}
/**
 * Formats colour for each element in array.
 * @param  {number} after array compared
 * @param  {number} before   array to compare with
 * @return {React.ReactNode}  Returns an array of constaints with appropriate className
 */
export function formatColorArray(
  after: string[] | undefined,
  before: string[],
): React.ReactElement<TagType>[] | null {
  let rows: React.ReactElement<TagType>[] = [];

  // record the differences
  if (!before && after) {
    after.forEach((a) => {
      generateTag('rollout-target__title-added', a, rows);
    });
    return rows;
  }

  if (!after && before) {
    before.forEach((a) => {
      generateTag('rollout-target__title-deleted', a, rows);
    });
    return rows;
  }

  //record deleted
  if (before) {
    const removed = before.filter((a) => !after!.includes(a));
    removed.forEach((a) => {
      generateTag('rollout-target__title-deleted', a, rows);
    });
  }

  //record added
  if (after) {
    const added = after
      .filter((a) => !before.includes(a))
      .reduce((res, obj) => {
        res[obj] = true;
        return res;
      }, {});
    after.forEach((a) => {
      if (added[a]) {
        generateTag('rollout-target__title-added', a, rows);
      } else {
        rows.push(<Tag key={a}>{a}</Tag>);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  let nullable_rows = rows.length > 0 ? rows : null;
  return nullable_rows;
}

/**
 * Helper function to generate Tag
 * @param  {string} className className to add to Tag
 * @param  {string} item   Item as body for Tag
 * @param  {any[]} rows   Array which contains Tags
 */
function generateTag(className: string, item: string, rows: any[]): void {
  if (item) {
    rows.push(
      <Tag className={className} key={item}>
        {item}
      </Tag>,
    );
    return;
  }
}

/**
 * Formats colour for each character in string to indicate difference
 * @param  {string} after string compared
 * @param  {string} before   string to compare with
 * @return {React.ReactNode}  Returns an array of span with appropriate className to highlight each character in string
 */
export function formatColorString(after: string, before: string): any {
  //LCS and string diff
  before = prettify(before);
  after = prettify(after);
  if (before.length * after.length > 500000000) {
    return (
      <div style={{ paddingTop: '10px' }}>
        <Alert
          type="info"
          message="Long string detected"
          description={
            <p>
              String is too large to calculate difference. Please use an
              external diff checker tool like{' '}
              <a
                href="https://www.diffchecker.com/"
                target="_blank"
                rel="noreferrer"
              >
                www.diffchecker.com
              </a>
            </p>
          }
          showIcon
        />
        <span>{after}</span>
      </div>
    );
  }
  const lookup = lcs(before, after);
  let res = [];
  res = stringDiff(before, after, before.length, after.length, lookup, res);
  return <span>{res}</span>;
}

/**
 * Helper to check if string is JSON
 * @param  {string} s string to check
 * @return {boolean}  True if is boolean, false otherwise
 */
function isJson(s: string): boolean {
  try {
    JSON.parse(s);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Helper to prettify JSON if input is JSON
 * @param  {string} s string to prettify
 * @return {boolean}  Prettified string
 */
export function prettify(s: string): string {
  if (isJson(s)) {
    return JSON.stringify(JSON.parse(s), null, 4);
  }
  return s;
}

/**
 * Diff utility to find minimum difference between two strings and highlight differences
 * @param  {string} str1 string 1
 * @param  {string} str2 string 2
 * @param  {number} m length of string 1
 * @param  {number} n length of string 2
 * @param  {any} lookup table generated by LCS on string 1 and string 2
 * @param  {any} res result array to store results
 * @return {any}  array of span of characters to indicate difference
 */
function stringDiff(
  str1: string,
  str2: string,
  m: number,
  n: number,
  lookup: any,
  res: any,
): any {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (m <= 0 && n <= 0) {
      return res.reverse();
    }
    if (m > 0 && n > 0 && str1[m - 1] == str2[n - 1]) {
      res.push(str1[m - 1]);
      m -= 1;
      n -= 1;
    } else if (n > 0 && (m == 0 || lookup[m][n - 1] >= lookup[m - 1][n])) {
      res.push(
        <span className="rollout-target__title-added" key={random_key()}>
          {str2[n - 1]}
        </span>,
      );
      n -= 1;
    } else if (m > 0 && (n == 0 || lookup[m][n - 1] < lookup[m - 1][n])) {
      res.push(
        <span className="rollout-target__title-deleted" key={random_key()}>
          {str1[m - 1]}
        </span>,
      );
      m -= 1;
    }
  }
}

/**
 * Algorithm for Longest Common substring between 2 strings. Runtime: O(mn)
 * @param  {string} str1 string 1
 * @param  {string} str2 string 2
 * @return {any}  returns memoized table
 */
export function lcs(str1: string, str2: string): any {
  const lookup = Array(str1.length + 1)
    .fill(null)
    .map(() => Array(str2.length + 1).fill(null));
  for (let i = 0; i <= str1.length; i += 1) {
    lookup[i][0] = 0;
  }
  for (let j = 0; j <= str2.length; j += 1) {
    lookup[0][j] = 0;
  }
  for (let i = 1; i <= str1.length; i += 1) {
    for (let j = 1; j <= str2.length; j += 1) {
      if (str1[i - 1] == str2[j - 1]) {
        lookup[i][j] = lookup[i - 1][j - 1] + 1;
      } else {
        lookup[i][j] = Math.max(lookup[i][j - 1], lookup[i - 1][j]);
      }
    }
  }
  return lookup;
}

/**
 * Helper function to generate random number between 0 and Number.MAX_VALUE
 * @return {number}  random number
 */
export function random_key(): number {
  return random(Number.MAX_VALUE);
}
